/* ═══════════════════════════════════════════════════════════════════════════════
   useTourManager: Generic Tour State Manager

   Handles:
   - Version gating (profile.claims_last_seen_app_version vs tour version)
   - New user vs returning user detection
   - Tour completion persistence (localStorage + Supabase JSONB)
   - Server hydration on load (restores state after cache clears)
   - Profile refresh after completion
   - AUTO-CHAINING: After completing one tour, automatically shows the next
     pending tour on the same page (no reload required)

   REUSABLE: Call with a page identifier and it handles the rest.

   Usage:
     const tour = useTourManager('dashboard');
     if (tour.activeTour) {
       <TourEngine tour={tour.activeTour} onComplete={tour.complete} onSkip={tour.skip} />
     }
   ═══════════════════════════════════════════════════════════════════════════════ */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';
import { CLAIMS_APP_VERSION } from '../lib/appVersion';
import {
  getPendingTours,
  markTourCompleted,
  getCompletedTours,
  getServerCompletedTours,
  hydrateLocalFromServer,
  isTourCompleted,
  TOUR_REGISTRY,
} from '../lib/tours/tourRegistry';
import type { TourDefinition, TourCompletionRecord } from '../lib/tours/types';
import { logError } from "../lib/logError";

/** Delay between chained tours (ms), breathing room for the user */
const TOUR_CHAIN_DELAY_MS = 1000;

interface UseTourManagerReturn {
  /** The currently active tour (null if none pending) */
  activeTour: TourDefinition | null;
  /** Whether a tour is currently showing */
  isActive: boolean;
  /** Complete the tour (marks as done in localStorage + Supabase JSONB) */
  complete: () => void;
  /** Skip the tour (marks as skipped in localStorage + Supabase JSONB) */
  skip: () => void;
  /** Number of remaining tours on this page (including current) */
  remaining: number;
}

export function useTourManager(page: string): UseTourManagerReturn {
  const { profile, user, refreshProfile } = useAuth();
  const [activeTour, setActiveTour] = useState<TourDefinition | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [hasChecked, setHasChecked] = useState(false);

  // Stable ref for profile to avoid stale closures in finishTour
  const profileRef = useRef(profile);
  useEffect(() => {
    profileRef.current = profile;
  }, [profile]);

  // ─── Hydrate localStorage from server + determine pending tours ────────────
  useEffect(() => {
    if (!profile || !user || hasChecked) return;

    // Delay check to let page render first
    const timeout = setTimeout(() => {
      // Step 1: Hydrate localStorage from server records (survives cache clears)
      hydrateLocalFromServer(profile.claims_completed_tours);

      // Step 2: Parse server records for the pending tour check
      const serverRecords = getServerCompletedTours(
        profile.claims_completed_tours,
      );

      // Step 3: Get pending tours using BOTH localStorage + server records
      const pending = getPendingTours(
        page,
        profile.claims_last_seen_app_version ?? null,
        profile.created_at,
        serverRecords,
      );

      setRemaining(pending.length);
      if (pending.length > 0) {
        setActiveTour(pending[0]); // Show highest priority tour first
      }
      setHasChecked(true);
    }, 800);

    return () => clearTimeout(timeout);
  }, [profile, user, page, hasChecked]);

  // ─── Persist completion to localStorage + Supabase JSONB ───────────────────
  const finishTour = useCallback(
    async (skipped: boolean) => {
      if (!activeTour || !user) return;

      const justFinishedId = activeTour.id;

      // 1. Mark in localStorage (fast local cache, synchronous)
      markTourCompleted(justFinishedId, skipped);

      // 2. Clear current tour immediately for clean UX
      setActiveTour(null);

      // 3. Persist to Supabase JSONB (survives cache clears)
      try {
        // Read current server records, add this completion, write back
        const currentLocal = getCompletedTours();
        const serverRecords = getServerCompletedTours(
          profileRef.current?.claims_completed_tours,
        );

        // Merge: take all server records + the new local ones
        const mergedMap = new Map<string, TourCompletionRecord>();
        for (const rec of serverRecords) mergedMap.set(rec.tourId, rec);
        for (const rec of currentLocal) mergedMap.set(rec.tourId, rec);
        const merged = Array.from(mergedMap.values());

        // Write merged records to Supabase
        const updatePayload: Record<string, unknown> = {
          claims_completed_tours: JSON.parse(JSON.stringify(merged)),
        };

        // Also update the version gate when ALL registered tours are done
        const allToursCompleted = TOUR_REGISTRY.every(t => isTourCompleted(t.id));
        if (allToursCompleted) {
          updatePayload.claims_last_seen_app_version = CLAIMS_APP_VERSION;
        }

        await supabase
          .from('profiles')
          .update(updatePayload)
          .eq('id', user.id);

        await refreshProfile();
      } catch (err) {
        logError('[Tour] Failed to persist to Supabase:', err);
        // Tour is still marked in localStorage, so it will persist on next completion
      }

      // 4. AUTO-CHAIN: Re-evaluate pending tours for this page.
      //    localStorage is already updated (step 1 is synchronous), so
      //    getPendingTours will correctly exclude the just-completed tour.
      const updatedServerRecords = getServerCompletedTours(
        profileRef.current?.claims_completed_tours,
      );
      const nextPending = getPendingTours(
        page,
        profileRef.current?.claims_last_seen_app_version ?? null,
        profileRef.current?.created_at ?? null,
        updatedServerRecords,
      );

      setRemaining(nextPending.length);

      if (nextPending.length > 0) {
        // Show next tour after a brief delay for breathing room
        setTimeout(() => {
          setActiveTour(nextPending[0]);
        }, TOUR_CHAIN_DELAY_MS);
      }
    },
    [activeTour, user, page, refreshProfile],
  );

  const complete = useCallback(() => finishTour(false), [finishTour]);
  const skip = useCallback(() => finishTour(true), [finishTour]);

  return {
    activeTour,
    isActive: activeTour !== null,
    complete,
    skip,
    remaining,
  };
}
